import React, { useState } from "react";
import ReactMarkdown from "react-markdown";

export interface DemoSetupJobData {
  status?: "pending" | "processing" | "complete" | "error" | string;
  proposal_json: any | null;
  insert_sql: string | null;
  delete_sql: string | null;
  roleplay_script: string | null;
  warnings?: string[] | null;
  tenant_id?: string | null;
  executed_at?: string | null;
  created?: string | null;
  tenant_name?: string | null;
  url?: string | null;
  created_by?: string | null;
}

interface LearnQuestion {
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: string;
  explanation?: string;
  correct_explanation?: string;
}

interface LearnLesson {
  title: string;
  emoji?: string;
  xp_reward?: number;
  questions: LearnQuestion[];
}

interface LearnUnit {
  title: string;
  emoji?: string;
  lessons: LearnLesson[];
}

const formatRoleplayMarkdown = (md: string | null): string => {
  if (!md) return "";
  let out = md.replace(/\r\n/g, "\n");
  out = out.replace(
    /([^\n])\n(?=(?:\*\*)?(?:Server|Customer)(?:\*\*)?:)/g,
    "$1\n\n",
  );
  out = out.replace(/([^\n])\n(?=\[)/g, "$1\n\n");
  return out;
};

const buildQuizCheatSheetMarkdown = (units: LearnUnit[]): string => {
  if (!units || units.length === 0) return "";
  const lines: string[] = ["", "---", "", "# Quiz Cheat Sheet", ""];
  units.forEach((unit, ui) => {
    lines.push(`## Unit ${ui + 1}: ${unit.emoji ?? ""} ${unit.title}`.trim());
    lines.push("");
    (unit.lessons || []).forEach((lesson, li) => {
      const xp = lesson.xp_reward ? ` _(+${lesson.xp_reward} XP)_` : "";
      lines.push(`### Lesson ${li + 1}: ${lesson.emoji ?? ""} ${lesson.title}${xp}`.trim());
      lines.push("");
      (lesson.questions || []).forEach((q, qi) => {
        const correct = (q.correct_option || "a").toLowerCase();
        lines.push(`**Q${qi + 1}.** ${q.question_text}`);
        lines.push("");
        const options: Array<["a" | "b" | "c" | "d", string]> = [
          ["a", q.option_a],
          ["b", q.option_b],
          ["c", q.option_c],
          ["d", q.option_d],
        ];
        // feat-346: a 2–3 option question leaves C/D blank — never list them.
        options.filter(([, text]) => (text ?? '').trim() !== '').forEach(([letter, text]) => {
          const marker = letter === correct ? "✅" : "  ";
          lines.push(`- ${marker} **${letter.toUpperCase()}.** ${text}`);
        });
        lines.push("");
      });
    });
  });
  return lines.join("\n");
};

interface RoleplayTabProps {
  roleplayScript: string | null;
  learnUnits: LearnUnit[];
  onCopy: (text: string, label: string) => void;
}

export const RoleplayTab: React.FC<RoleplayTabProps> = ({ roleplayScript, learnUnits, onCopy }) => {
  const cheatSheet = buildQuizCheatSheetMarkdown(learnUnits);
  const formattedScript = formatRoleplayMarkdown(roleplayScript) || "No roleplay script generated.";
  const combinedMarkdown = formattedScript + (cheatSheet ? "\n" + cheatSheet : "");

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => onCopy(combinedMarkdown, "Roleplay + Cheat Sheet")}
          className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          Copy Roleplay + Cheat Sheet
        </button>
        <button
          onClick={() => onCopy(formattedScript, "Roleplay Script")}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
        >
          Copy Roleplay Only
        </button>
        {cheatSheet && (
          <button
            onClick={() => onCopy(cheatSheet.trimStart(), "Quiz Cheat Sheet")}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Copy Cheat Sheet Only
          </button>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown>{formattedScript}</ReactMarkdown>
        </div>
      </div>

      {learnUnits && learnUnits.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Quiz Cheat Sheet</h2>
          <p className="text-sm text-gray-500 mb-4">
            Every Learn-tab question for this tenant with the correct answer marked. Use this to
            blow through the Learn module during the demo.
          </p>
          <div className="space-y-6">
            {learnUnits.map((unit, ui) => (
              <div key={ui} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 border-b border-blue-100 px-4 py-2">
                  <p className="font-semibold text-blue-900">
                    Unit {ui + 1}: {unit.emoji} {unit.title}
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {(unit.lessons || []).map((lesson, li) => (
                    <div key={li} className="p-4">
                      <div className="flex items-baseline justify-between mb-3">
                        <p className="font-medium text-gray-900">
                          Lesson {li + 1}: {lesson.emoji} {lesson.title}
                        </p>
                        {lesson.xp_reward != null && (
                          <span className="text-xs text-gray-500">+{lesson.xp_reward} XP</span>
                        )}
                      </div>
                      <ol className="space-y-3 list-decimal list-inside">
                        {(lesson.questions || []).map((q, qi) => {
                          const correct = (q.correct_option || "a").toLowerCase();
                          const options: Array<["a" | "b" | "c" | "d", string]> = [
                            ["a", q.option_a],
                            ["b", q.option_b],
                            ["c", q.option_c],
                            ["d", q.option_d],
                          ];
                          return (
                            <li key={qi} className="text-sm">
                              <span className="font-medium text-gray-900">{q.question_text}</span>
                              <ul className="mt-1 ml-4 space-y-0.5">
                                {options.filter(([, text]) => (text ?? '').trim() !== '').map(([letter, text]) => {
                                  const isCorrect = letter === correct;
                                  return (
                                    <li
                                      key={letter}
                                      className={
                                        isCorrect
                                          ? "text-green-700 font-semibold"
                                          : "text-gray-600"
                                      }
                                    >
                                      <span className="inline-block w-5">
                                        {isCorrect ? "✅" : ""}
                                      </span>
                                      <span className="font-medium mr-1">
                                        {letter.toUpperCase()}.
                                      </span>
                                      {text}
                                    </li>
                                  );
                                })}
                              </ul>
                            </li>
                          );
                        })}
                      </ol>
                      {(!lesson.questions || lesson.questions.length === 0) && (
                        <p className="text-sm text-gray-400 italic">No questions in this lesson.</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ProposalView: React.FC<{ proposal: any }> = ({ proposal }) => {
  if (!proposal) return <p className="text-gray-500">No proposal data.</p>;

  return (
    <div className="space-y-6">
      {proposal.upsell_targets && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Upsell Targets</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {proposal.upsell_targets.map((t: any, i: number) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{t.emoji}</span>
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-gray-500">{t.metric_key} - ${t.price}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{t.description}</p>
                {t.applies_to && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.applies_to.map((a: string) => (
                      <span key={a} className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">
                        {a}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {proposal.drink_promotion && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Drink Promotion</h3>
          <div className="border border-gray-200 rounded-lg p-4 inline-block">
            <span className="text-2xl mr-2">{proposal.drink_promotion.emoji}</span>
            <span className="font-semibold">{proposal.drink_promotion.name}</span>
            <span className="text-gray-500 ml-2">${proposal.drink_promotion.price}</span>
          </div>
        </div>
      )}

      {proposal.greeting_phrases && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Greeting Phrases</h3>
          <ul className="space-y-2">
            {proposal.greeting_phrases.map((p: any, i: number) => (
              <li key={i} className="bg-gray-50 p-3 rounded text-sm">
                "{p.phrase}"
                <span className="text-gray-400 ml-2 text-xs">highlights: {p.highlight_words}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {proposal.upsell_phrases && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Upsell Phrases</h3>
          <ul className="space-y-2">
            {proposal.upsell_phrases.map((p: any, i: number) => (
              <li key={i} className="bg-gray-50 p-3 rounded text-sm">
                <span className="text-xs text-blue-600 font-medium mr-2">[{p.metric_key}]</span>
                "{p.phrase}"
                <span className="text-gray-400 ml-2 text-xs">highlights: {p.highlight_words}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {proposal.shift_challenge && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Shift Challenge</h3>
          <div className="bg-gray-50 p-4 rounded">
            <p className="font-medium">{proposal.shift_challenge.name}</p>
            <p className="text-sm text-gray-600">
              Target: {proposal.shift_challenge.target_count} |
              Est. tip/item: ${proposal.shift_challenge.estimated_tip_per_item} |
              Close rate: {(proposal.shift_challenge.close_rate * 100).toFixed(0)}%
            </p>
          </div>
        </div>
      )}

      {proposal.prompt_fields?.service_criteria && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Service Criteria (Metrics)</h3>
          <div className="space-y-2">
            {(proposal.prompt_fields.service_criteria.metrics || []).map((m: any, i: number) => (
              <div key={i} className="bg-gray-50 p-3 rounded">
                <p className="font-medium">
                  <span className="mr-2">{m.emoji}</span>
                  {m.label} ({m.key})
                </p>
                <p className="text-sm text-gray-600 mt-1">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface DemoSetupJobViewerProps {
  job: DemoSetupJobData;
}

const DemoSetupJobViewer: React.FC<DemoSetupJobViewerProps> = ({ job }) => {
  const [activeTab, setActiveTab] = useState<"proposal" | "insert" | "delete" | "roleplay">("roleplay");
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch {
      setCopySuccess("Failed to copy");
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden">
      {job.warnings && job.warnings.length > 0 && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
          <p className="text-yellow-800 font-medium text-sm">Warnings:</p>
          {job.warnings.map((w, i) => (
            <p key={i} className="text-yellow-700 text-sm ml-2">- {w}</p>
          ))}
        </div>
      )}

      {(job.created || job.created_by || job.url) && (
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 text-sm text-gray-600">
          {job.tenant_name && <span className="mr-4"><strong>Tenant:</strong> {job.tenant_name}</span>}
          {job.url && <span className="mr-4"><strong>URL:</strong> {job.url}</span>}
          {job.created_by && <span className="mr-4"><strong>Created by:</strong> {job.created_by}</span>}
          {job.created && <span className="mr-4"><strong>Created:</strong> {job.created}</span>}
          {job.executed_at && <span><strong>Executed:</strong> {job.executed_at}</span>}
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          {([
            ["roleplay", "Roleplay + Quiz"],
            ["proposal", "Proposal"],
            ["insert", "Insert SQL"],
            ["delete", "Delete SQL"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {copySuccess && (
          <div className="fixed top-4 right-4 bg-gray-800 text-white px-4 py-2 rounded shadow-lg z-50 text-sm">
            {copySuccess === "Failed to copy" ? copySuccess : `Copied ${copySuccess}!`}
          </div>
        )}

        {activeTab === "proposal" && <ProposalView proposal={job.proposal_json} />}

        {activeTab === "insert" && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => copyToClipboard(job.insert_sql || "", "Insert SQL")}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed max-h-[600px] overflow-y-auto">
              {job.insert_sql || "(none)"}
            </pre>
          </div>
        )}

        {activeTab === "delete" && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => copyToClipboard(job.delete_sql || "", "Delete SQL")}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Copy to Clipboard
              </button>
            </div>
            <pre className="bg-gray-900 text-red-400 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed max-h-[600px] overflow-y-auto">
              {job.delete_sql || "(none)"}
            </pre>
          </div>
        )}

        {activeTab === "roleplay" && (
          <RoleplayTab
            roleplayScript={job.roleplay_script}
            learnUnits={job.proposal_json?.learn_units || []}
            onCopy={copyToClipboard}
          />
        )}
      </div>
    </div>
  );
};

export default DemoSetupJobViewer;
